import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import type { CabinState, SemanticIntentResponse, ServiceId } from "../types/domain";

export type RealtimeVoicePhase =
  | "idle"
  | "connecting"
  | "asr"
  | "understanding"
  | "speaking"
  | "done"
  | "error";

export interface RealtimeVoiceContext {
  activeServiceId?: ServiceId | null;
  state: CabinState;
  topServiceId?: ServiceId | null;
}

export interface RealtimeVoiceFinalIntent {
  semantic: SemanticIntentResponse;
  transcript: string;
}

interface UseRealtimeVoiceGatewayOptions {
  getContext: () => RealtimeVoiceContext;
  onFallbackTranscript: (transcript: string) => void;
  onFinalTranscript?: (transcript: string) => boolean;
  onFinalIntent: (payload: RealtimeVoiceFinalIntent) => void;
}

interface RealtimeVoiceGatewayEvent {
  type: string;
  provider?: string;
  sessionId?: string;
  text?: string;
  semantic?: SemanticIntentResponse;
  transcript?: string;
  message?: string;
}

const defaultGatewayUrl = "ws://127.0.0.1:8787/voice";
const sameOriginGatewayValue = "__SAME_ORIGIN__";
const targetAudioSampleRate = 16000;

export function useRealtimeVoiceGateway({
  getContext,
  onFallbackTranscript,
  onFinalTranscript,
  onFinalIntent
}: UseRealtimeVoiceGatewayOptions) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const audioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const pendingTranscriptRef = useRef("");
  const handledFinalRef = useRef(false);
  const suppressServerFinalRef = useRef(false);
  const optionsRef = useRef({
    getContext,
    onFallbackTranscript,
    onFinalTranscript,
    onFinalIntent
  });
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioListening, setAudioListening] = useState(false);
  const [audioSupported, setAudioSupported] = useState(false);
  const [phase, setPhase] = useState<RealtimeVoicePhase>("idle");
  const [provider, setProvider] = useState("mock");
  const [replyText, setReplyText] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState("");

  useEffect(() => {
    optionsRef.current = {
      getContext,
      onFallbackTranscript,
      onFinalTranscript,
      onFinalIntent
    };
  }, [getContext, onFallbackTranscript, onFinalTranscript, onFinalIntent]);

  useEffect(() => {
    setAudioSupported(
      typeof navigator !== "undefined" &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      Boolean(getAudioContextConstructor())
    );

    return () => {
      releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
      socketRef.current?.close();
    };
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setAudioListening(false);
    setPhase("idle");
    setReplyText("");
    setSessionId(null);
    setTranscript("");
  }, []);

  const stop = useCallback(() => {
    handledFinalRef.current = true;
    suppressServerFinalRef.current = false;
    releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
    setAudioListening(false);
    socketRef.current?.send(JSON.stringify({ type: "session.stop" }));
    socketRef.current?.close();
    socketRef.current = null;
    setConnected(false);
    setPhase("idle");
  }, []);

  const wireGatewaySocket = useCallback((socket: WebSocket, fallbackTranscript: string) => {
    socket.onmessage = (event) => {
      const payload = parseGatewayEvent(event.data);

      if (!payload) {
        return;
      }

      if (payload.type === "asr.final" && payload.text) {
        const handledLocally = optionsRef.current.onFinalTranscript?.(payload.text) ?? false;

        if (handledLocally) {
          handledFinalRef.current = true;
          suppressServerFinalRef.current = true;

          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "session.stop" }));
          }
        }
      }

      handleGatewayEvent(payload, {
        markFinalHandled: () => {
          handledFinalRef.current = true;
        },
        onFinalIntent: optionsRef.current.onFinalIntent,
        setConnected,
        setError,
        setPhase,
        setProvider,
        setReplyText,
        setSessionId,
        setTranscript,
        shouldSuppressServerFinal: () => suppressServerFinalRef.current
      });

      if (payload.type === "asr.final" && suppressServerFinalRef.current) {
        setPhase("speaking");
      }

      if (payload.type === "asr.final" || payload.type === "asr.error" || payload.type === "session.done" || payload.type === "session.stopped") {
        releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
        setAudioListening(false);
      }
    };

    socket.onerror = () => {
      setError(fallbackTranscript ? "实时语音网关不可用，已切回本地处理。" : "实时语音网关不可用，请用文字测试。");
      setPhase("error");
      releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
      setAudioListening(false);

      if (fallbackTranscript && !handledFinalRef.current) {
        handledFinalRef.current = true;
        optionsRef.current.onFallbackTranscript(fallbackTranscript);
      }
    };

    socket.onclose = () => {
      setConnected(false);
      releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
      setAudioListening(false);

      if (fallbackTranscript && !handledFinalRef.current) {
        handledFinalRef.current = true;
        optionsRef.current.onFallbackTranscript(fallbackTranscript);
      }
    };
  }, []);

  const runTranscript = useCallback((rawTranscript: string) => {
    const nextTranscript = rawTranscript.trim();

    if (!nextTranscript) {
      return;
    }

    if (typeof WebSocket === "undefined") {
      optionsRef.current.onFallbackTranscript(nextTranscript);
      return;
    }

    handledFinalRef.current = true;
    closeSocketSilently(socketRef);
    pendingTranscriptRef.current = nextTranscript;
    handledFinalRef.current = false;
    suppressServerFinalRef.current = false;
    setConnected(false);
    setError(null);
    setPhase("connecting");
    setProvider("mock");
    setReplyText("");
    setSessionId(null);
    setTranscript("");

    const gatewayUrl = resolveVoiceGatewayUrl();
    const socket = new WebSocket(gatewayUrl);
    socketRef.current = socket;
    wireGatewaySocket(socket, nextTranscript);

    socket.onopen = () => {
      setConnected(true);
      setError(null);
      socket.send(JSON.stringify({
        type: "session.start",
        transcript: nextTranscript,
        context: optionsRef.current.getContext()
      }));
    };
  }, [wireGatewaySocket]);

  const startAudioSession = useCallback(async () => {
    if (!audioSupported) {
      setError("当前浏览器不支持麦克风流式上传。");
      setPhase("error");
      return;
    }

    handledFinalRef.current = true;
    suppressServerFinalRef.current = false;
    closeSocketSilently(socketRef);
    releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      const gatewayUrl = resolveVoiceGatewayUrl();
      const socket = new WebSocket(gatewayUrl);

      socketRef.current = socket;
      audioStreamRef.current = stream;
      pendingTranscriptRef.current = "";
      handledFinalRef.current = false;
      suppressServerFinalRef.current = false;
      setConnected(false);
      setError(null);
      setPhase("connecting");
      setProvider("mock");
      setReplyText("");
      setSessionId(null);
      setTranscript("");
      wireGatewaySocket(socket, "");

      socket.onopen = () => {
        setConnected(true);
        setError(null);
        socket.send(JSON.stringify({
          type: "session.start",
          mode: "audio",
          audioFormat: "pcm",
          sampleRate: targetAudioSampleRate,
          context: optionsRef.current.getContext()
        }));

        startPcmAudioStream({
          audioContextRef,
          audioProcessorRef,
          audioSourceRef,
          socket,
          stream
        }).then(() => {
          setAudioListening(true);
          setPhase("asr");
        }).catch((streamError) => {
          releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
          setAudioListening(false);
          setError(streamError instanceof Error ? `麦克风流启动失败：${streamError.message}` : "麦克风流启动失败。");
          setPhase("error");

          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "session.stop" }));
          }
        });
      };
    } catch (audioError) {
      releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
      setAudioListening(false);
      setError(audioError instanceof Error ? `麦克风不可用：${audioError.message}` : "麦克风不可用。");
      setPhase("error");
    }
  }, [audioSupported, wireGatewaySocket]);

  const stopAudioSession = useCallback(() => {
    releaseAudioInput(audioStreamRef, audioContextRef, audioSourceRef, audioProcessorRef);
    setAudioListening(false);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "session.stop" }));
    }
  }, []);

  return {
    audioListening,
    audioSupported,
    connected,
    error,
    phase,
    provider,
    replyText,
    reset,
    runTranscript,
    sessionId,
    startAudioSession,
    stop,
    stopAudioSession,
    transcript
  };
}

function resolveVoiceGatewayUrl() {
  const configuredGatewayUrl = import.meta.env.VITE_VOICE_GATEWAY_URL;

  if (
    configuredGatewayUrl === sameOriginGatewayValue ||
    configuredGatewayUrl === "/voice" ||
    configuredGatewayUrl === "same-origin"
  ) {
    return getSameOriginVoiceGatewayUrl();
  }

  if (configuredGatewayUrl?.startsWith("/")) {
    return getSameOriginVoiceGatewayUrl(configuredGatewayUrl);
  }

  return configuredGatewayUrl || defaultGatewayUrl;
}

function getSameOriginVoiceGatewayUrl(pathname = "/voice") {
  if (typeof window === "undefined") {
    return defaultGatewayUrl;
  }

  const gatewayUrl = new URL(pathname, window.location.origin);
  gatewayUrl.protocol = gatewayUrl.protocol === "https:" ? "wss:" : "ws:";
  return gatewayUrl.toString();
}

function releaseAudioInput(
  audioStreamRef: MutableRefObject<MediaStream | null>,
  audioContextRef: MutableRefObject<AudioContext | null>,
  audioSourceRef: MutableRefObject<MediaStreamAudioSourceNode | null>,
  audioProcessorRef: MutableRefObject<ScriptProcessorNode | null>
) {
  audioProcessorRef.current?.disconnect();
  audioProcessorRef.current = null;
  audioSourceRef.current?.disconnect();
  audioSourceRef.current = null;
  void audioContextRef.current?.close();
  audioContextRef.current = null;
  audioStreamRef.current?.getTracks().forEach((track) => track.stop());
  audioStreamRef.current = null;
}

async function startPcmAudioStream({
  audioContextRef,
  audioProcessorRef,
  audioSourceRef,
  socket,
  stream
}: {
  audioContextRef: MutableRefObject<AudioContext | null>;
  audioProcessorRef: MutableRefObject<ScriptProcessorNode | null>;
  audioSourceRef: MutableRefObject<MediaStreamAudioSourceNode | null>;
  socket: WebSocket;
  stream: MediaStream;
}) {
  const AudioContextConstructor = getAudioContextConstructor();

  if (!AudioContextConstructor) {
    throw new Error("当前浏览器不支持 WebAudio。");
  }

  const audioContext = new AudioContextConstructor();
  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(2048, 1, 1);

  processor.onaudioprocess = (event) => {
    event.outputBuffer.getChannelData(0).fill(0);

    if (socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const input = event.inputBuffer.getChannelData(0);
    const pcmBuffer = convertFloat32ToPcm16(input, audioContext.sampleRate, targetAudioSampleRate);

    if (pcmBuffer.byteLength) {
      socket.send(pcmBuffer);
    }
  };

  source.connect(processor);
  processor.connect(audioContext.destination);

  if (audioContext.state === "suspended") {
    await audioContext.resume();
  }

  audioContextRef.current = audioContext;
  audioSourceRef.current = source;
  audioProcessorRef.current = processor;
}

function convertFloat32ToPcm16(input: Float32Array, inputSampleRate: number, outputSampleRate: number) {
  const sampleRate = inputSampleRate > 0 ? inputSampleRate : outputSampleRate;
  const ratio = sampleRate / outputSampleRate;
  const outputLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Int16Array(outputLength);

  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const start = Math.floor(outputIndex * ratio);
    const end = Math.min(input.length, Math.floor((outputIndex + 1) * ratio));
    let sum = 0;
    let count = 0;

    for (let inputIndex = start; inputIndex < end; inputIndex += 1) {
      sum += input[inputIndex];
      count += 1;
    }

    const sample = Math.max(-1, Math.min(1, count > 0 ? sum / count : input[start] || 0));
    output[outputIndex] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }

  return output.buffer;
}

function getAudioContextConstructor() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.AudioContext || (window as typeof window & {
    webkitAudioContext?: typeof AudioContext;
  }).webkitAudioContext || null;
}

function closeSocketSilently(socketRef: MutableRefObject<WebSocket | null>) {
  const socket = socketRef.current;

  if (!socket) {
    return;
  }

  socket.onclose = null;
  socket.onerror = null;
  socket.onmessage = null;
  socket.close();
  socketRef.current = null;
}

function parseGatewayEvent(data: unknown): RealtimeVoiceGatewayEvent | null {
  if (typeof data !== "string") {
    return null;
  }

  try {
    return JSON.parse(data) as RealtimeVoiceGatewayEvent;
  } catch {
    return null;
  }
}

function handleGatewayEvent(
  event: RealtimeVoiceGatewayEvent,
  setters: {
    markFinalHandled: () => void;
    onFinalIntent: (payload: RealtimeVoiceFinalIntent) => void;
    setConnected: (connected: boolean) => void;
    setError: (error: string | null) => void;
    setPhase: (phase: RealtimeVoicePhase) => void;
    setProvider: (provider: string) => void;
    setReplyText: (updater: string | ((current: string) => string)) => void;
    setSessionId: (sessionId: string | null) => void;
    setTranscript: (transcript: string) => void;
    shouldSuppressServerFinal: () => boolean;
  }
) {
  if (event.provider) {
    setters.setProvider(event.provider);
  }

  if (event.sessionId) {
    setters.setSessionId(event.sessionId);
  }

  if (event.type === "session.started") {
    setters.setConnected(true);
    setters.setPhase("asr");
    setters.setReplyText("");
    return;
  }

  if (event.type === "asr.partial" && event.text) {
    setters.setPhase("asr");
    setters.setTranscript(event.text);
    return;
  }

  if (event.type === "asr.final" && event.text) {
    setters.setPhase("understanding");
    setters.setTranscript(event.text);
    return;
  }

  if (event.type === "asr.error") {
    setters.setError(event.message || "没有识别到有效语音，请再说一次。");
    setters.setPhase("error");
    return;
  }

  if (event.type === "nlu.partial") {
    if (setters.shouldSuppressServerFinal()) {
      return;
    }

    setters.setPhase("understanding");
    return;
  }

  if (event.type === "nlu.final" && event.semantic && event.transcript) {
    if (setters.shouldSuppressServerFinal()) {
      return;
    }

    setters.markFinalHandled();
    setters.setPhase("speaking");
    setters.onFinalIntent({
      semantic: event.semantic,
      transcript: event.transcript
    });
    return;
  }

  if (event.type === "tts.start") {
    if (setters.shouldSuppressServerFinal()) {
      return;
    }

    setters.setPhase("speaking");
    setters.setReplyText("");
    return;
  }

  if (event.type === "tts.chunk" && typeof event.text === "string") {
    if (setters.shouldSuppressServerFinal()) {
      return;
    }

    setters.setPhase("speaking");
    setters.setReplyText((current) => `${current}${event.text}`);
    return;
  }

  if (event.type === "tts.done" || event.type === "session.done") {
    setters.setPhase("done");
    return;
  }

  if (event.type === "session.stopped") {
    setters.setPhase("idle");
    return;
  }

  if (event.type === "error") {
    setters.setError(event.message || "实时语音网关错误。");
    setters.setPhase("error");
  }
}
